import {TextInput, FormControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useState, useRef, useCallback, type FormEvent, useEffect, useEffectEvent} from 'react'
import {useTagValidation} from './use-tag-validation'
import styles from './CreateTagDialog.module.css'

export interface CreateTagDialogProps {
  onDismiss: () => void
  onCreate: (tagName: string) => void
  initialTagName?: string
  tagValidationUrl: string
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

export function CreateTagDialog({
  onDismiss,
  onCreate,
  initialTagName,
  tagValidationUrl,
  returnFocusRef,
}: CreateTagDialogProps) {
  const [tagName, setTagName] = useState(initialTagName)
  const hasInteracted = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const trimmedTagName = tagName?.trim()

  const {state, message, validateTag} = useTagValidation(tagValidationUrl)

  const isLoading = state === 'loading'
  // Empty validation message should only show after user interaction, not on initial dialog render
  // eslint-disable-next-line react-hooks/refs
  const isEmptyAfterInput = state === 'empty' && hasInteracted.current
  const isValid = state === 'valid' || state === 'pending' || state === 'branch_exists'
  const isInvalid = state === 'invalid' || state === 'immutable' || state === 'duplicate' || isEmptyAfterInput

  const showValidation = !isLoading && message && (isValid || isInvalid)

  // Validate pre-filled tag input immediately on mount
  const validateInitialTag = useEffectEvent(() => {
    if (initialTagName?.trim()) {
      validateTag(initialTagName.trim())
    }
  })
  useEffect(() => {
    validateInitialTag()
  }, []) // Only run on mount so no dependencies needed

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setTagName(newValue)
      hasInteracted.current = true

      validateTag(newValue.trim())
    },
    [validateTag],
  )
  const handleCreate = useCallback(() => {
    if (trimmedTagName) {
      onCreate(trimmedTagName)
      onDismiss()
    }
  }, [trimmedTagName, onCreate, onDismiss])

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      // Only submit if validation passes
      if (!isLoading && !isInvalid) {
        handleCreate()
      }
    },
    [handleCreate, isLoading, isInvalid],
  )

  return (
    <Dialog
      onClose={onDismiss}
      title="Create new tag"
      width="medium"
      className={styles.Dialog}
      initialFocusRef={inputRef}
      returnFocusRef={returnFocusRef}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onDismiss},
        {
          buttonType: 'primary',
          content: 'Create',
          onClick: handleCreate,
          disabled: isLoading || isInvalid,
          type: 'submit',
        },
      ]}
    >
      <form onSubmit={handleSubmit}>
        <FormControl required>
          <FormControl.Label>Tag</FormControl.Label>
          <TextInput
            ref={inputRef}
            value={tagName}
            onChange={handleChange}
            aria-label="Tag name"
            block
            loading={isLoading}
          />
          {showValidation && (
            <FormControl.Validation variant={isInvalid ? 'error' : 'success'}>{message}</FormControl.Validation>
          )}
          {state === 'branch_exists' && (
            <FormControl.Caption>
              Creating a tag with the same name as a branch can cause ambiguity when selecting refs
            </FormControl.Caption>
          )}
        </FormControl>
      </form>
    </Dialog>
  )
}
